"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { alpha, styled, useTheme } from "@mui/material/styles";

import {
  Add,
  Edit,
  Delete,
  Refresh,
  Close,
  Save,
  Search,
  FilterList,
} from "@mui/icons-material";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { useBranch } from "./../../layouts/BranchContext";

/* =========================================================
   CUSTOM TABLE SCROLLBAR
   Used everywhere a scrollable area appears: the table
   container and all modal scroll areas.
========================================================= */

const CustomTableScrollbar = styled("div")(({ theme }) => ({
  "&::-webkit-scrollbar": {
    width: "6px",
    height: "6px",
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.2)"
        : "rgba(0,0,0,0.2)",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.3)"
        : "rgba(0,0,0,0.3)",
  },
  scrollbarWidth: "thin",
  scrollbarColor: `${
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.2)"
      : "rgba(0,0,0,0.2)"
  } transparent`,
}));

/* Scrollable modal bodies use the same scrollbar. */

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  "&::-webkit-scrollbar": {
    width: "6px",
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.2)"
        : "rgba(0,0,0,0.2)",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.3)"
        : "rgba(0,0,0,0.3)",
  },
  scrollbarWidth: "thin",
  scrollbarColor: `${
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.2)"
      : "rgba(0,0,0,0.2)"
  } transparent`,
}));

/* =========================================================
   API
========================================================= */

const API_BASE_URL = "https://wims.revisetoexcel.com/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

/* =========================================================
   ACCESS TOKEN
========================================================= */

const getAccessToken = () => {
  if (typeof window === "undefined") return null;

  const keys = ["accessToken", "access_token", "token"];

  for (const key of keys) {
    const localToken = localStorage.getItem(key);

    if (localToken) return localToken;

    const sessionToken = sessionStorage.getItem(key);

    if (sessionToken) return sessionToken;
  }

  return null;
};

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();

    if (token) {
      config.headers = config.headers || {};

      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* =========================================================
   USER
========================================================= */

const getStoredUser = () => {
  if (typeof window === "undefined") return null;

  try {
    const storedUser =
      sessionStorage.getItem("user") || localStorage.getItem("user");

    if (!storedUser) return null;

    return JSON.parse(storedUser);
  } catch (error) {
    console.error("User parse error:", error);

    return null;
  }
};

/* =========================================================
   RESPONSE HELPERS
========================================================= */

const getArray = (response) => {
  const data = response?.data;

  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.data)) return data.data;

  if (Array.isArray(data?.results)) return data.results;

  if (Array.isArray(data?.categories)) return data.categories;

  return [];
};

/* =========================================================
   EMPTY FORM
========================================================= */

const emptyForm = {
  organization: "",
  branch: "",
  category_name: "",
  description: "",
};

/* =========================================================
   COMPONENT
========================================================= */

function ExpenseCategory() {
  const theme = useTheme();

  /* Global branch selector — replaces all local branch
     state, branch dropdowns and fetchBranches calls. */

  const { selectedBranchId } = useBranch();

  const isDark = theme.palette.mode === "dark";

  const textPrimary = theme.palette.text.primary;

  const borderColor = theme.palette.divider;

  const inputBackground = theme.palette.background.paper;

  /* =======================================================
     USER
  ======================================================= */

  const [currentUser, setCurrentUser] = useState(null);

  const [organizationId, setOrganizationId] = useState("");

  /* =======================================================
     DATA
  ======================================================= */

  const [categories, setCategories] = useState([]);

  /* =======================================================
     LOADING
  ======================================================= */

  const [loading, setLoading] = useState(false);

  const [saving, setSaving] = useState(false);

  /* Shows the table loader from first render until the
     initial fetch completes. */

  const [initialLoading, setInitialLoading] = useState(true);

  /* =======================================================
     SEARCH — client-side (list endpoint has no `search`
     param in the Swagger spec)
  ======================================================= */

  const [searchText, setSearchText] = useState("");

  /* =======================================================
     FILTERS
  ======================================================= */

  const [filterModalOpen, setFilterModalOpen] = useState(false);

  /* "" = all, "true" / "false" — sent to the API as a
     real boolean. */

  const [filterStatus, setFilterStatus] = useState("");

  /* =======================================================
     PAGINATION (client-side)
  ======================================================= */

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* =======================================================
     ADD / EDIT
  ======================================================= */

  const [openDialog, setOpenDialog] = useState(false);

  const [editMode, setEditMode] = useState(false);

  const [selectedId, setSelectedId] = useState(null);

  const [formData, setFormData] = useState(emptyForm);

  /* =======================================================
     BULK CREATE
  ======================================================= */

  const [openBulkDialog, setOpenBulkDialog] = useState(false);

  const [bulkRows, setBulkRows] = useState([
    { category_name: "", description: "" },
  ]);

  /* =======================================================
     DELETE
  ======================================================= */

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const [deleteId, setDeleteId] = useState(null);

  /* =======================================================
     SNACKBAR
  ======================================================= */

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  /* =======================================================
     STYLES
  ======================================================= */

  const dialogPaperStyles = {
    backgroundColor: theme.palette.background.paper,
    color: textPrimary,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: isDark
      ? "0 20px 50px rgba(0,0,0,0.45)"
      : "0 20px 50px rgba(0,0,0,0.12)",
  };

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      color: textPrimary,
      backgroundColor: inputBackground,
      "& fieldset": { borderColor },
      "&:hover fieldset": { borderColor: theme.palette.primary.main },
      "&.Mui-focused fieldset": { borderColor: theme.palette.primary.main },
    },
    "& .MuiInputBase-input": {
      color: textPrimary,
      WebkitTextFillColor: textPrimary,
    },
    "& .MuiInputBase-input::placeholder": {
      color: textPrimary,
      opacity: 0.75,
    },
    "& .MuiInputLabel-root": { color: textPrimary },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
  };

  const selectStyles = {
    borderRadius: 2,
    backgroundColor: inputBackground,
    color: textPrimary,
    "& .MuiOutlinedInput-notchedOutline": { borderColor },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "& .MuiSvgIcon-root": { color: textPrimary },
  };

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        color: textPrimary,
        border: `1px solid ${theme.palette.divider}`,
        "& .MuiMenuItem-root": {
          color: textPrimary,
          "&:hover": { backgroundColor: theme.palette.action.hover },
          "&.Mui-selected": {
            backgroundColor: isDark
              ? "rgba(255,255,255,0.12)"
              : "rgba(0,0,0,0.08)",
            "&:hover": {
              backgroundColor: isDark
                ? "rgba(255,255,255,0.16)"
                : "rgba(0,0,0,0.12)",
            },
          },
        },
      },
    },
  };

  /* =======================================================
     ERROR HELPERS
  ======================================================= */

  const formatFieldLabel = (field) => {
    const labels = {
      category_name: "Category Name",
      description: "Description",
      organization: "Organization",
      branch: "Branch",
      is_active: "Status",
      categories: "Categories",
    };

    return (
      labels[field] ||
      field
        ?.replaceAll("_", " ")
        ?.replace(/\b\w/g, (char) => char.toUpperCase())
    );
  };

  const getErrorMessage = (error) => {
    if (error?.response?.status === 401) {
      return "Unauthorized. Please login again.";
    }

    const backendErrors = error?.response?.data?.errors;

    if (
      backendErrors &&
      typeof backendErrors === "object" &&
      Object.keys(backendErrors).length > 0
    ) {
      return Object.entries(backendErrors)
        .map(([field, messages]) => {
          const message = Array.isArray(messages)
            ? messages.join(" ")
            : typeof messages === "string"
            ? messages
            : Object.values(messages)
                .flat(Infinity)
                .map((item) => String(item))
                .join(" ");

          return `${formatFieldLabel(field)}: ${message}`;
        })
        .join(" | ");
    }

    if (error?.response?.data?.detail) {
      return error.response.data.detail;
    }

    if (error?.response?.data?.message) {
      return error.response.data.message;
    }

    if (error?.response?.data?.error) {
      return error.response.data.error;
    }

    if (error?.response?.data) {
      const data = error.response.data;

      if (typeof data === "string") {
        return data;
      }

      return Object.values(data)
        .flat(Infinity)
        .map((item) => String(item))
        .join(" ");
    }

    return error?.message || "Something went wrong.";
  };

  /* =======================================================
     SNACKBAR
  ======================================================= */

  const showSnackbar = useCallback(
    (message, severity = "success") => {
      setSnackbar({ open: true, message, severity });
    },
    []
  );

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  /* =======================================================
     READ LOGGED-IN USER (organization only — branch now
     comes from the global selector)
  ======================================================= */

  useEffect(() => {
    const user = getStoredUser();

    if (!user) {
      console.warn("Logged-in user not found.");
      return;
    }

    setCurrentUser(user);

    const orgId =
      user.organization_id || user.organization || "";

    setOrganizationId(orgId ? String(orgId) : "");
  }, []);

  /* =======================================================
     FETCH CATEGORIES
     GET /library/expense-category/list/
     params: organization (int), branch (int),
             is_active (boolean)
  ======================================================= */

  const fetchCategories = useCallback(
    async (overrideStatus) => {
      if (!organizationId) return;

      setLoading(true);

      try {
        const params = {
          organization: Number(organizationId),
        };

        if (selectedBranchId) {
          params.branch = Number(selectedBranchId);
        }

        const status = overrideStatus !== undefined ? overrideStatus : filterStatus;

        if (status !== "") {
          params.is_active = status === "true";
        }

        const response = await api.get(
          "/library/expense-category/list/",
          { params }
        );

        setCategories(getArray(response));
      } catch (error) {
        console.error("Expense Category List Error:", error);

        showSnackbar(getErrorMessage(error), "error");
      } finally {
        setLoading(false);

        setInitialLoading(false);
      }
    },
    [organizationId, selectedBranchId, filterStatus, showSnackbar]
  );

  /* Re-fetch whenever the global branch or organization
     changes (also runs on mount). */

  useEffect(() => {
    if (!organizationId) return;

    fetchCategories();
  }, [organizationId, selectedBranchId]);

  /* =======================================================
     SEARCH — client-side filter
  ======================================================= */

  const filteredCategories = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    if (!search) return categories;

    return categories.filter((category) => {
      const name = category.category_name || "";

      const description = category.description || "";

      const organization =
        category.organization_name ||
        String(category.organization || "");

      const branch =
        category.branch_name || String(category.branch || "");

      return (
        name.toLowerCase().includes(search) ||
        description.toLowerCase().includes(search) ||
        organization.toLowerCase().includes(search) ||
        branch.toLowerCase().includes(search)
      );
    });
  }, [categories, searchText]);

  const displayedCategories = filteredCategories.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /* =======================================================
     ADD / EDIT
  ======================================================= */

  const handleOpenCreate = () => {
    setEditMode(false);

    setSelectedId(null);

    setFormData({
      ...emptyForm,
      organization: organizationId || "",
      branch: selectedBranchId || "",
    });

    setOpenDialog(true);
  };

  const handleOpenEdit = (category) => {
    setEditMode(true);

    setSelectedId(category.id);

    setFormData({
      organization: String(category.organization || organizationId || ""),
      branch: String(category.branch || selectedBranchId || ""),
      category_name: category.category_name || "",
      description: category.description || "",
    });

    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    if (saving) return;

    setOpenDialog(false);

    setSelectedId(null);

    setEditMode(false);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /* =======================================================
     VALIDATE
  ======================================================= */

  const validateForm = () => {
    if (!formData.organization) {
      showSnackbar("Organization is required.", "error");

      return false;
    }

    if (!formData.branch) {
      showSnackbar(
        "Branch is required. Please select a branch from the global branch selector.",
        "error"
      );

      return false;
    }

    if (!formData.category_name.trim()) {
      showSnackbar("Category name is required.", "error");

      return false;
    }

    return true;
  };

  /* =======================================================
     CREATE
     POST /library/expense-category/create/
     { organization: int, branch: int, category_name,
       description }
  ======================================================= */

  const handleCreate = async () => {
    if (!validateForm()) return;

    setSaving(true);

    try {
      const payload = {
        organization: Number(formData.organization),
        branch: Number(formData.branch),
        category_name: formData.category_name.trim(),
        description: formData.description?.trim() || "",
      };

      await api.post("/library/expense-category/create/", payload);

      showSnackbar("Expense category created successfully.", "success");

      setOpenDialog(false);

      setFormData({
        ...emptyForm,
        organization: organizationId || "",
        branch: selectedBranchId || "",
      });

      await fetchCategories();
    } catch (error) {
      console.error("Create Expense Category Error:", error);

      showSnackbar(getErrorMessage(error), "error");
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     UPDATE
     PUT /library/expense-category/update/{id}/
     { organization: int, branch: int, category_name,
       description }
  ======================================================= */

  const handleUpdate = async () => {
    if (!validateForm()) return;

    if (!selectedId) return;

    setSaving(true);

    try {
      const payload = {
        organization: Number(formData.organization),
        branch: Number(formData.branch),
        category_name: formData.category_name.trim(),
        description: formData.description?.trim() || "",
      };

      await api.put(
        `/library/expense-category/update/${selectedId}/`,
        payload
      );

      showSnackbar("Expense category updated successfully.", "success");

      setOpenDialog(false);

      await fetchCategories();
    } catch (error) {
      console.error("Update Expense Category Error:", error);

      showSnackbar(getErrorMessage(error), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (editMode) {
      handleUpdate();
    } else {
      handleCreate();
    }
  };

  /* =======================================================
     STATUS
     PATCH /library/expense-category/status/{id}/
     { is_active: boolean }
  ======================================================= */

  const handleStatusChange = async (category) => {
    const nextActive = !category.is_active;

    /* Optimistic update */

    setCategories((prev) =>
      prev.map((item) =>
        item.id === category.id ? { ...item, is_active: nextActive } : item
      )
    );

    try {
      await api.patch(
        `/library/expense-category/status/${category.id}/`,
        { is_active: nextActive }
      );

      showSnackbar(
        `Category ${nextActive ? "activated" : "deactivated"} successfully.`,
        "success"
      );
    } catch (error) {
      console.error("Status Update Error:", error);

      /* Roll back on failure */

      setCategories((prev) =>
        prev.map((item) =>
          item.id === category.id
            ? { ...item, is_active: category.is_active }
            : item
        )
      );

      showSnackbar(getErrorMessage(error), "error");
    }
  };

  /* =======================================================
     DELETE (soft delete)
     DELETE /library/expense-category/delete/{id}/
  ======================================================= */

  const handleOpenDelete = (id) => {
    setDeleteId(id);

    setOpenDeleteDialog(true);
  };

  const handleCloseDelete = () => {
    if (saving) return;

    setDeleteId(null);

    setOpenDeleteDialog(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    setSaving(true);

    try {
      await api.delete(`/library/expense-category/delete/${deleteId}/`);

      showSnackbar("Expense category deleted successfully.", "success");

      setOpenDeleteDialog(false);

      setDeleteId(null);

      await fetchCategories();
    } catch (error) {
      console.error("Delete Expense Category Error:", error);

      showSnackbar(getErrorMessage(error), "error");
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     BULK CREATE
     POST /library/expense-category/bulk-create/
     { categories: [{ organization: int, branch: int,
         category_name, description }] }
  ======================================================= */

  const handleBulkChange = (index, field, value) => {
    setBulkRows((prev) => {
      const updated = [...prev];

      updated[index] = { ...updated[index], [field]: value };

      return updated;
    });
  };

  const addBulkRow = () => {
    setBulkRows((prev) => [
      ...prev,
      { category_name: "", description: "" },
    ]);
  };

  const removeBulkRow = (index) => {
    if (bulkRows.length === 1) return;

    setBulkRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleBulkCreate = async () => {
    const invalidRow = bulkRows.some(
      (row) => !row.category_name.trim()
    );

    if (invalidRow) {
      showSnackbar("Category name is required for every row.", "error");

      return;
    }

    if (!organizationId) {
      showSnackbar("Organization is required.", "error");

      return;
    }

    if (!selectedBranchId) {
      showSnackbar(
        "Branch is required. Please select a branch from the global branch selector.",
        "error"
      );

      return;
    }

    setSaving(true);

    try {
      const payload = {
        categories: bulkRows.map((row) => ({
          organization: Number(organizationId),
          branch: Number(selectedBranchId),
          category_name: row.category_name.trim(),
          description: row.description?.trim() || "",
        })),
      };

      await api.post(
        "/library/expense-category/bulk-create/",
        payload
      );

      showSnackbar("Expense categories created successfully.", "success");

      setOpenBulkDialog(false);

      setBulkRows([{ category_name: "", description: "" }]);

      await fetchCategories();
    } catch (error) {
      console.error("Bulk Create Error:", error);

      showSnackbar(getErrorMessage(error), "error");
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     FILTERS
  ======================================================= */

  const handleApplyFilter = () => {
    setPage(0);

    fetchCategories();

    setFilterModalOpen(false);
  };

  const handleResetFilter = () => {
    setFilterStatus("");

    setSearchText("");

    setPage(0);

    fetchCategories("");

    setFilterModalOpen(false);
  };

  const handleRefresh = () => {
    fetchCategories();
  };

  const handleClearSearch = () => {
    setSearchText("");

    setPage(0);
  };

  /* =======================================================
     PAGINATION
  ======================================================= */

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));

    setPage(0);
  };

  /* =======================================================
     DISPLAY HELPERS
  ======================================================= */

  const currentBranchName =
    categories.find(
      (item) => String(item.branch) === String(selectedBranchId)
    )?.branch_name ||
    currentUser?.branch_name ||
    (selectedBranchId ? `Branch ${selectedBranchId}` : "-");

  /* =======================================================
     UI
  ======================================================= */

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        minHeight: "100vh",
        color: textPrimary,
      }}
    >
      {/* ===================================================
          PAGE HEADER
      =================================================== */}

      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "stretch", lg: "center" },
          gap: 1.5,
          mb: 3,
          flexWrap: { xs: "wrap", lg: "nowrap" },
          width: "100%",
        }}
      >
        {/* SEARCH — client-side, live filtering */}

        <TextField
          size="small"
          variant="outlined"
          value={searchText}
          onChange={(event) => {
            setSearchText(event.target.value);
            setPage(0);
          }}
          placeholder="Search category, description..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: theme.palette.primary.main }} />
              </InputAdornment>
            ),
            endAdornment: (
              searchText && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    sx={{ color: textPrimary }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            ),
          }}
          sx={{
            flex: { xs: "1 1 100%", sm: "1 1 340px", lg: "1 1 auto" },
            minWidth: { lg: 280 },
            maxWidth: { lg: 380 },
            "& .MuiOutlinedInput-root": {
              height: 50,
              borderRadius: "30px",
              backgroundColor: inputBackground,
              color: textPrimary,
              "& input": {
                color: textPrimary,
                WebkitTextFillColor: textPrimary,
              },
              "& input::placeholder": {
                color: textPrimary,
                opacity: 0.75,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: isDark
                  ? "rgba(255,255,255,0.28)"
                  : "rgba(0,0,0,0.22)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.primary.main,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderWidth: 2,
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />

        {/* ACTIONS */}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            flexShrink: 0,
            width: { xs: "100%", lg: "auto" },
            justifyContent: { xs: "flex-start", lg: "flex-end" },
            ml: { lg: "auto" },
          }}
        >
          {/* REFRESH */}

          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{
              height: 50,
              px: 2.5,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              boxShadow: "none",
              flexShrink: 0,
              "&:hover": { boxShadow: "none" },
            }}
          >
            Refresh
          </Button>

          {/* FILTER */}

          <Button
            variant="contained"
            startIcon={<FilterList />}
            onClick={() => setFilterModalOpen(true)}
            sx={{
              height: 50,
              px: 2.5,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              boxShadow: "none",
              flexShrink: 0,
              "&:hover": { boxShadow: "none" },
            }}
          >
            Filters
          </Button>

          {/* BULK CREATE */}

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpenBulkDialog(true)}
            sx={{
              height: 50,
              px: 2.5,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              boxShadow: "none",
              flexShrink: 0,
              "&:hover": { boxShadow: "none" },
            }}
          >
            Bulk Create
          </Button>

          {/* ADD */}

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCreate}
            sx={{
              height: 50,
              px: 2.5,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              boxShadow: "none",
              flexShrink: 0,
              "&:hover": { boxShadow: "none" },
            }}
          >
            Add Category
          </Button>
        </Box>
      </Box>

      {/* ===================================================
          TABLE
      =================================================== */}

      <Card
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <CardContent
          sx={{
            p: 0,
            "&:last-child": { pb: 0 },
          }}
        >
          {/* MAIN TABLE — scrollable via CustomTableScrollbar */}

          <CustomTableScrollbar
            sx={{
              overflowX: "auto",
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Table stickyHeader sx={{ minWidth: 1000 }}>
              <TableHead>
                               <TableRow
                  sx={{
                    "& .MuiTableCell-root": {
                      backgroundColor: theme.palette.primary.main,
                      color: "#FFFFFF",
                      fontWeight: 700,
                      fontSize: "0.82rem",
                      borderBottom: "none",
                      py: 1.8,
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableCell>S.No.</TableCell>

                  <TableCell>Category Name</TableCell>

                  <TableCell>Description</TableCell>

                 

                  <TableCell>Status</TableCell>

                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading || initialLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align="center"
                      sx={{ py: 7, borderBottom: "none" }}
                    >
                      <CircularProgress
                        size={32}
                        sx={{ color: theme.palette.primary.main }}
                      />

                      <Typography sx={{ mt: 1.5, color: textPrimary }}>
                        Loading expense categories...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : displayedCategories.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align="center"
                      sx={{ py: 8, borderBottom: "none" }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.95rem",
                          color: textPrimary,
                        }}
                      >
                        No expense categories found
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ mt: 0.7, color: textPrimary }}
                      >
                        Try changing your search or filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedCategories.map((category, index) => (
                    <TableRow key={category.id} hover>
                      {/* S.NO */}

                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        {page * rowsPerPage + index + 1}
                      </TableCell>

                      {/* CATEGORY NAME */}

                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Typography
                          fontWeight={600}
                          sx={{ color: textPrimary }}
                        >
                          {category.category_name || "-"}
                        </Typography>
                      </TableCell>

                      {/* DESCRIPTION */}

                      <TableCell sx={{ py: 1.5, px: 2, maxWidth: 320 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: textPrimary,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: 320,
                          }}
                        >
                          {category.description || "-"}
                        </Typography>
                      </TableCell>

                     
                      {/* STATUS */}

                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            px: 1.2,
                            py: 0.45,
                            borderRadius: "999px",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color: category.is_active
                              ? theme.palette.success.dark
                              : theme.palette.error.dark,
                            backgroundColor: category.is_active
                              ? alpha(
                                  theme.palette.success.main,
                                  isDark ? 0.18 : 0.12
                                )
                              : alpha(
                                  theme.palette.error.main,
                                  isDark ? 0.18 : 0.12
                                ),
                            border: `1px solid ${
                              category.is_active
                                ? alpha(theme.palette.success.main, 0.35)
                                : alpha(theme.palette.error.main, 0.35)
                            }`,
                          }}
                        >
                          {category.is_active ? "Active" : "Inactive"}
                        </Box>
                      </TableCell>

                      {/* ACTIONS */}

                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            justifyContent: "center",
                          }}
                        >
                          {/* EDIT */}

                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEdit(category)}
                              sx={{
                                color: theme.palette.primary.main,
                                border: `1px solid ${alpha(
                                  theme.palette.primary.main,
                                  0.3
                                )}`,
                                borderRadius: 1.5,
                                "&:hover": {
                                  backgroundColor: alpha(
                                    theme.palette.primary.main,
                                    isDark ? 0.15 : 0.08
                                  ),
                                },
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {/* STATUS SWITCH */}

                          <Tooltip
                            title={
                              category.is_active ? "Deactivate" : "Activate"
                            }
                          >
                            <Switch
                              size="small"
                              checked={category.is_active === true}
                              onChange={() => handleStatusChange(category)}
                              sx={{
                                "& .MuiSwitch-switchBase.Mui-checked": {
                                  color: theme.palette.success.main,
                                },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                  {
                                    backgroundColor:
                                      theme.palette.success.main,
                                    opacity: 0.5,
                                  },
                              }}
                            />
                          </Tooltip>

                          {/* DELETE */}

                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDelete(category.id)}
                              sx={{
                                color: theme.palette.error.main,
                                border: `1px solid ${alpha(
                                  theme.palette.error.main,
                                  0.3
                                )}`,
                                borderRadius: 1.5,
                                "&:hover": {
                                  backgroundColor: alpha(
                                    theme.palette.error.main,
                                    isDark ? 0.15 : 0.08
                                  ),
                                },
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CustomTableScrollbar>

          {/* PAGINATION — client-side */}

          <TablePagination
            component="div"
            count={filteredCategories.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              color: textPrimary,
              borderTop: `1px solid ${borderColor}`,
              "& .MuiTablePagination-selectIcon": {
                color: textPrimary,
              },
              "& .MuiTablePagination-actions button": {
                color: textPrimary,
              },
            }}
          />
        </CardContent>
      </Card>

      {/* ===================================================
          FILTER DIALOG
      =================================================== */}

      <Dialog
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperStyles }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Filter Expense Categories
          </Typography>

          <IconButton
            onClick={() => setFilterModalOpen(false)}
            sx={{ color: textPrimary }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <Divider />

        <StyledDialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
              mt: 1,
            }}
          >
            {/* ORGANIZATION — read-only */}

            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  display: "block",
                  mb: 1,
                  letterSpacing: 0.5,
                  color: theme.palette.primary.main,
                }}
              >
                ORGANIZATION
              </Typography>

              <Box
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  px: 2,
                  py: 1.25,
                  backgroundColor: theme.palette.action.hover,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: textPrimary }}
                >
                  {currentUser?.organization_name || organizationId || "-"}
                </Typography>
              </Box>
            </Box>

            {/* BRANCH — from global selector, read-only */}

            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  display: "block",
                  mb: 1,
                  letterSpacing: 0.5,
                  color: theme.palette.primary.main,
                }}
              >
                BRANCH
              </Typography>

              <Box
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  px: 2,
                  py: 1.25,
                  backgroundColor: theme.palette.action.hover,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: textPrimary }}
                >
                  {currentBranchName}
                </Typography>
              </Box>

              <Typography
                variant="caption"
                sx={{ mt: 0.5, display: "block", color: textPrimary }}
              >
                Change the branch from the global branch selector in the
                layout.
              </Typography>
            </Box>

            {/* STATUS — sent to the API as a boolean */}

            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  display: "block",
                  mb: 1,
                  letterSpacing: 0.5,
                  color: theme.palette.primary.main,
                }}
              >
                STATUS
              </Typography>

              <FormControl fullWidth size="small">
                <Select
                  value={filterStatus}
                  onChange={(event) => setFilterStatus(event.target.value)}
                  displayEmpty
                  MenuProps={menuProps}
                  sx={selectStyles}
                >
                  <MenuItem value="">All Statuses</MenuItem>

                  <MenuItem value="true">Active</MenuItem>

                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </StyledDialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            variant="contained"
            onClick={handleResetFilter}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              color: textPrimary,
              borderColor: theme.palette.divider,
              "&:hover": {
                borderColor: textPrimary,
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            Reset
          </Button>

          <Button
            variant="contained"
            onClick={handleApplyFilter}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              px: 3,
            }}
          >
            Apply Filter
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          ADD / EDIT DIALOG
      =================================================== */}

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            ...dialogPaperStyles,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            {editMode ? "Edit Category" : "Add Category"}
          </Typography>

          <IconButton
            size="small"
            onClick={handleCloseDialog}
            disabled={saving}
            sx={{
              color: textPrimary,
              border: `1px solid ${theme.palette.divider}`,
              "&:hover": { backgroundColor: theme.palette.action.hover },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider />

        <StyledDialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
              py: 2,
            }}
          >
           

            {/* CATEGORY NAME */}

            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  color: theme.palette.primary.main,
                }}
              >
                CATEGORY NAME
              </Typography>

              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Office Supplies"
                name="category_name"
                value={formData.category_name}
                onChange={handleFormChange}
                required
                inputProps={{ maxLength: 100 }}
                sx={textFieldStyles}
              />
            </Box>

            {/* DESCRIPTION */}

            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  color: theme.palette.primary.main,
                }}
              >
                DESCRIPTION{" "}
                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    ml: 0.8,
                    color: textPrimary,
                    fontWeight: 400,
                    textTransform: "none",
                  }}
                >
                  (Optional)
                </Typography>
              </Typography>

              <TextField
                fullWidth
                size="small"
                placeholder="Enter a short description..."
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                multiline
                rows={4}
                sx={textFieldStyles}
              />
            </Box>
          </Box>
        </StyledDialogContent>

        <Divider />

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1.2,
          }}
        >
          <Button
            variant="contained"
            onClick={handleCloseDialog}
            disabled={saving}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              color: textPrimary,
              borderColor: theme.palette.divider,
              "&:hover": {
                borderColor: textPrimary,
                backgroundColor: theme.palette.action.hover,
              },
            }}
           data-app-cancel="true">
            Cancel
          </Button>

          <Button
            variant="contained"
            startIcon={
              saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <Save fontSize="small" />
              )
            }
            onClick={handleSave}
            disabled={saving}
            sx={{
              minWidth: 120,
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              "& .MuiSvgIcon-root": { color: "inherit" },
            }}
          >
            {saving
              ? "Saving..."
              : editMode
              ? "Update Category"
              : "Create Category"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          BULK CREATE DIALOG
      =================================================== */}

      <Dialog
        open={openBulkDialog}
        onClose={() => {
          if (!saving) setOpenBulkDialog(false);
        }}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            ...dialogPaperStyles,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Bulk Create Categories
          </Typography>

          <IconButton
            size="small"
            onClick={() => setOpenBulkDialog(false)}
            disabled={saving}
            sx={{
              color: textPrimary,
              border: `1px solid ${theme.palette.divider}`,
              "&:hover": { backgroundColor: theme.palette.action.hover },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider />

        <StyledDialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              py: 2,
            }}
          >

           
            {/* DIVIDER TITLE */}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  color: textPrimary,
                  fontSize: "0.95rem",
                }}
              >
                Categories
              </Typography>

              <Chip
                label={`${bulkRows.length} ${
                  bulkRows.length === 1 ? "Category" : "Categories"
                }`}
                size="small"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                  backgroundColor: `${theme.palette.primary.main}15`,
                }}
              />
            </Box>

            {/* BULK ROWS */}

            <box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                maxHeight: 420,
                overflowY: "auto",
                pr: 0.5,
                
              }}
            >
              {bulkRows.map((row, index) => (
                <Box
                  key={index}
                  sx={{
                    position: "relative",
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2.5,
                    p: 2.5,
                  mb: 3,
                    backgroundColor: theme.palette.background.paper,
                  }}
                >
                  {/* ROW HEADER */}

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: theme.palette.primary.main,
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: "0.8rem",
                        }}
                      >
                        {index + 1}
                      </Box>

                      <Typography
                        sx={{ fontWeight: 700, color: textPrimary }}
                      >
                        Category {index + 1}
                      </Typography>
                    </Box>

                    {bulkRows.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => removeBulkRow(index)}
                        disabled={saving}
                        sx={{
                          color: theme.palette.error.main,
                          backgroundColor: `${theme.palette.error.main}12`,
                          "&:hover": {
                            backgroundColor: `${theme.palette.error.main}20`,
                          },
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </Box>

                  {/* FORM FIELDS */}

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    {/* CATEGORY NAME */}

                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mb: 1,
                          fontWeight: 700,
                          letterSpacing: 0.5,
                          color: theme.palette.primary.main,
                        }}
                      >
                        CATEGORY NAME
                      </Typography>

                      <TextField
                        fullWidth
                        size="small"
                        placeholder="e.g. Office Supplies"
                        value={row.category_name}
                        onChange={(event) =>
                          handleBulkChange(
                            index,
                            "category_name",
                            event.target.value
                          )
                        }
                        disabled={saving}
                        inputProps={{ maxLength: 100 }}
                        sx={textFieldStyles}
                      />
                    </Box>

                    {/* DESCRIPTION */}

                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mb: 1,
                          fontWeight: 700,
                          letterSpacing: 0.5,
                          color: theme.palette.primary.main,
                        }}
                      >
                        DESCRIPTION{" "}
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{
                            ml: 0.7,
                            color: textPrimary,
                            fontWeight: 400,
                            textTransform: "none",
                          }}
                        >
                          (Optional)
                        </Typography>
                      </Typography>

                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter description..."
                        value={row.description}
                        onChange={(event) =>
                          handleBulkChange(
                            index,
                            "description",
                            event.target.value
                          )
                        }
                        disabled={saving}
                        sx={textFieldStyles}
                      />
                    </Box>
                  </Box>
                </Box>
              ))}
            </box>


           
          </Box>
        </StyledDialogContent>

        <Divider />

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>

             <Button
              variant="contained"
              onClick={addBulkRow}
              disabled={saving}
              sx={{
                alignSelf: "flex-start",
                textTransform: "none",
                borderRadius: 2,
                fontWeight: 600,
                px: 2.5,
                borderStyle: "dashed",
                color: "inherit",
                borderColor: theme.palette.primary.main,
                "&:hover": {
                  borderStyle: "dashed",
                  backgroundColor: `${theme.palette.primary.main}10`,
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              + Add Another Category
            </Button>

            <Button
              variant="text"
              onClick={() => setOpenBulkDialog(false)}
              disabled={saving}
              sx={{
                textTransform: "none",
                color: textPrimary,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
             data-app-cancel="true">
              Cancel
            </Button>

            <Button
              variant="contained"
              startIcon={
                saving ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <Save />
                )
              }
              onClick={handleBulkCreate}
              disabled={saving}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 2.5,
                fontWeight: 600,
                "& .MuiSvgIcon-root": { color: "inherit" },
              }}
            >
              {saving
                ? "Creating..."
                : `Create ${bulkRows.length} ${
                    bulkRows.length === 1 ? "Category" : "Categories"
                  }`}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          DELETE DIALOG
      =================================================== */}

      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDelete}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            ...dialogPaperStyles,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Delete Expense Category
        </DialogTitle>

        <Divider />

        <StyledDialogContent sx={{ py: 3 }}>
          <Typography sx={{ color: textPrimary }}>
            Are you sure you want to delete this expense category?
          </Typography>

          <Typography
            variant="body2"
            sx={{ mt: 1, color: textPrimary }}
          >
            The category will be soft deleted and will not be permanently
            removed.
          </Typography>
        </StyledDialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            variant="contained"
            onClick={handleCloseDelete}
            disabled={saving}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              color: textPrimary,
              borderColor: theme.palette.divider,
              "&:hover": {
                borderColor: textPrimary,
                backgroundColor: theme.palette.action.hover,
              },
            }}
           data-app-cancel="true">
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            startIcon={
              saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <Delete />
              )
            }
            onClick={handleDelete}
            disabled={saving}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          SNACKBAR
      =================================================== */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ExpenseCategory;